/**
 * Pie renderer: slices of a whole, as a donut (default) or a solid pie.
 *
 * Drawn the way the correlation matrix is - in a uPlot `draw` hook over an
 * invisible series, with both axes hidden. A pie has no axes to speak of, but
 * the uPlot shell is what carries the resize machinery, grid panels, dashboard
 * tiles, the tooltip plumbing and image export, and none of those should need a
 * second implementation for one chart type.
 *
 * The legend is painted on the canvas beside the circle rather than as uPlot's
 * DOM legend, for the same reason: an exported pie whose slices are not named
 * is a picture of colours.
 *
 * Three rules this follows:
 *
 * 1. **Slices start at twelve o'clock and run clockwise, in picker order.** The
 *    order a person arranged is the order they read in; sorting by size would
 *    move a generator's colour every time the load shifted.
 * 2. **Signals take series colours, bands take the ramp.** Signals are
 *    categories and want to be told apart; bands are ordered low to high, and a
 *    ramp says that without a legend.
 * 3. **Nothing is drawn as nothing.** A signal left out for being negative or
 *    zero is still listed, greyed, with the reason - otherwise the remaining
 *    slices read as the whole.
 */

import uPlot from 'uplot';
import { plotState, type PlotState } from '../../../state/plot-state.ts';
import { PLOT_COLORS } from '../plot-constants.ts';
import { colorScaleRgb } from './color-scale.ts';
import { runtime } from '../plot-runtime.ts';
import { colorLegend } from '../plot-ui-state.ts';
import { themeColors, chartContainerSize, type ThemeColors } from './chart-dom.ts';
import { rebuildUplot } from './uplot-helpers.ts';
import { escHtml } from './format.ts';
import type { PieResponse, PieSlice } from '../../../api/types.ts';
import { placeTooltip } from './tooltip.ts';

/** Ring thickness as a share of the radius: thick enough to read, hole big enough for the total. */
const DONUT_HOLE = 0.58;

/** Gap drawn between neighbouring slices, in CSS px. */
const SLICE_GAP_PX = 1.5;

/** How far the hovered slice is pulled out, in CSS px. */
const HOVER_PULL_PX = 6;

/** A share this small gets no label on the slice - it is in the legend. */
const LABEL_MIN_ANGLE = 0.32;

/**
 * Largest radius as a share of the chart height. A ring filling a tall chart
 * edge to edge reads as a poster, and pushes the legend out of eye range.
 */
const MAX_RADIUS_SHARE = 0.4;

/** Legend goes to the right once the chart is this much wider than tall. */
const LEGEND_RIGHT_ASPECT = 1.25;

const TAU = Math.PI * 2;
const START = -Math.PI / 2;

/** One slice ready to draw: what it is called, its colour and its arc. */
export interface DrawnSlice {
  key: string;
  label: string;
  value: number;
  count: number;
  share: number;
  color: string;
  a0: number;
  a1: number;
}

/** Compact number for a legend or the centre: 18.4k, 1.25M, 0.042. */
export function compactNum(v: number): string {
  if (!Number.isFinite(v)) return '-';
  const abs = Math.abs(v);
  const trim = (s: string): string => s.replace(/\.0+$|(\.\d*?)0+$/, '$1');
  if (abs >= 1e9) return `${trim((v / 1e9).toFixed(2))}B`;
  if (abs >= 1e6) return `${trim((v / 1e6).toFixed(2))}M`;
  if (abs >= 1e4) return `${trim((v / 1e3).toFixed(1))}k`;
  if (abs >= 100) return String(Math.round(v));
  if (abs >= 1) return trim(v.toFixed(2));
  if (abs === 0) return '0';
  return trim(v.toPrecision(2));
}

/** A share as a percentage, with a decimal only where a whole number would lie. */
export function formatShare(share: number): string {
  const pct = share * 100;
  if (pct > 0 && pct < 1) return '<1%';
  if (pct > 99 && pct < 100) return `${pct.toFixed(1)}%`;
  return `${Math.round(pct)}%`;
}

/** "< 25", "25 – 50", "≥ 75": one band in words, open on the outer sides. */
export function bandLabel(lo: number | null, hi: number | null): string {
  if (lo == null && hi == null) return 'all values';
  if (lo == null) return `< ${compactNum(hi!)}`;
  if (hi == null) return `≥ ${compactNum(lo)}`;
  return `${compactNum(lo)} – ${compactNum(hi)}`;
}

/**
 * Slices in the order they are drawn.
 *
 * Signals follow the picker (`pie_columns`), so reordering there redraws from
 * what is loaded instead of re-querying; anything the picker does not list
 * keeps the response's order after the ones it does. Bands are already ordered
 * low to high.
 */
export function orderSlices(data: PieResponse, state: PlotState): PieSlice[] {
  if (data.mode !== 'signals') return data.slices.slice();
  const pos = new Map(state.pie_columns.map((c, i) => [c, i]));
  return data.slices
    .map((sl, i) => ({ sl, i }))
    .sort((a, b) => (pos.get(a.sl.key) ?? 1e6 + a.i) - (pos.get(b.sl.key) ?? 1e6 + b.i))
    .map((x) => x.sl);
}

/** Slices with shares, colours and angles, starting at twelve o'clock. */
export function layoutSlices(data: PieResponse, state: PlotState): DrawnSlice[] {
  const ordered = orderSlices(data, state);
  const total = ordered.reduce((sum, sl) => sum + Math.max(0, sl.value), 0);
  const scale = state.color_scale || 'Viridis';
  const out: DrawnSlice[] = [];
  let a = START;
  ordered.forEach((sl, i) => {
    const share = total > 0 ? Math.max(0, sl.value) / total : 0;
    const color =
      data.mode === 'bands'
        ? colorScaleRgb(scale, ordered.length > 1 ? 0.08 + (0.84 * i) / (ordered.length - 1) : 0.5)
        : (state.custom_colors[sl.key] ?? PLOT_COLORS[i % PLOT_COLORS.length] ?? '#6384ff');
    const a1 = a + share * TAU;
    out.push({
      key: sl.key,
      label: data.mode === 'bands' ? bandLabel(sl.lo, sl.hi) : sl.key,
      value: sl.value,
      count: sl.count,
      share,
      color,
      a0: a,
      a1,
    });
    a = a1;
  });
  return out;
}

/**
 * Which slice an angle falls in. `angle` is measured the canvas way (0 at three
 * o'clock, clockwise), so it is brought into the slices' range first.
 */
export function sliceIndexAt(slices: DrawnSlice[], angle: number): number {
  let t = angle;
  while (t < START) t += TAU;
  while (t >= START + TAU) t -= TAU;
  return slices.findIndex((sl) => sl.share > 0 && t >= sl.a0 && t < sl.a1);
}

/** What the number in the hole is, in words - so "59.5" is never unexplained. */
export function centreCaption(data: PieResponse): string {
  if (data.mode === 'bands') return 'rows';
  return data.agg === 'sum' ? 'total' : 'sum of means';
}

/** Where the circle and the legend go inside the plot box (canvas px). */
export interface PieLayout {
  cx: number;
  cy: number;
  r: number;
  legend: { x: number; y: number; w: number; rowH: number; side: 'right' | 'bottom'; rows: number };
}

export function pieLayout(
  box: { left: number; top: number; width: number; height: number },
  rows: number,
  fontPx: number,
  legendTextPx: number,
): PieLayout {
  const pad = Math.round(fontPx * 1.2);
  const gap = pad * 2;
  if (rows <= 0) {
    const r = Math.max(8, Math.min(box.width / 2 - pad, box.height * MAX_RADIUS_SHARE));
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    return { cx, cy, r, legend: { x: cx, y: cy, w: 0, rowH: 0, side: 'right', rows: 0 } };
  }
  const rowH = Math.round(fontPx * 1.75);
  const swatch = Math.round(fontPx * 0.9);
  const legendW = Math.min(Math.round(box.width * 0.45), legendTextPx + swatch + pad);
  const right = box.width >= box.height * LEGEND_RIGHT_ASPECT;
  if (right) {
    // The ring and its legend are one group, centred together: a legend pinned
    // to the far edge of a wide chart is read as belonging to nothing.
    const r = Math.max(
      8,
      Math.min(box.height * MAX_RADIUS_SHARE, (box.width - legendW - gap) / 2 - pad),
    );
    const groupW = 2 * r + gap + legendW;
    const x0 = box.left + Math.max(pad, (box.width - groupW) / 2);
    const cy = box.top + box.height / 2;
    const shown = Math.max(1, Math.min(rows, Math.floor((box.height - pad) / rowH)));
    return {
      cx: x0 + r,
      cy,
      r,
      legend: {
        x: x0 + 2 * r + gap,
        y: cy - (shown * rowH) / 2,
        w: legendW,
        rowH,
        side: 'right',
        rows: shown,
      },
    };
  }
  // Below: as many rows as fit under a circle that keeps at least half the box.
  const maxLegendH = box.height * 0.45;
  const shown = Math.max(1, Math.min(rows, Math.floor(maxLegendH / rowH)));
  const legendH = shown * rowH;
  const r = Math.max(
    8,
    Math.min(box.width / 2 - pad, (box.height - legendH - gap) / 2, box.height * MAX_RADIUS_SHARE),
  );
  const groupH = 2 * r + gap + legendH;
  const y0 = box.top + Math.max(pad / 2, (box.height - groupH) / 2);
  const w = Math.min(legendW, box.width - pad * 2);
  return {
    cx: box.left + box.width / 2,
    cy: y0 + r,
    r,
    legend: {
      x: box.left + (box.width - w) / 2,
      y: y0 + 2 * r + gap,
      w,
      rowH,
      side: 'bottom',
      rows: shown,
    },
  };
}

/** Readable text over a slice: dark on pale colours, light on dark ones. */
function textOn(rgb: string): string {
  let r = 0;
  let g = 0;
  let b = 0;
  const m = /rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(rgb);
  if (m) {
    [r, g, b] = [Number(m[1]), Number(m[2]), Number(m[3])];
  } else if (/^#[0-9a-f]{6}$/i.test(rgb)) {
    r = parseInt(rgb.slice(1, 3), 16);
    g = parseInt(rgb.slice(3, 5), 16);
    b = parseInt(rgb.slice(5, 7), 16);
  }
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#12151a' : '#ffffff';
}

function ellipsize(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (ctx.measureText(`${text.slice(0, mid)}…`).width <= maxW) lo = mid;
    else hi = mid - 1;
  }
  return `${text.slice(0, Math.max(1, lo))}…`;
}

/** Hover state shared between the draw hook and the cursor hook of one chart. */
interface PieHover {
  index: number;
}

/** The draw hook that paints the pie and its legend. Shared by the chart, panels and tiles. */
export function pieDrawHook(
  data: PieResponse,
  c: ThemeColors,
  state: PlotState,
  hover: PieHover,
  geometry: { layout: PieLayout | null; slices: DrawnSlice[] },
): (u: uPlot) => void {
  const slices = layoutSlices(data, state);
  geometry.slices = slices;
  const donut = state.style.pie_donut;

  return (u: uPlot): void => {
    const ctx = u.ctx;
    const d = uPlot.pxRatio || 1;
    const fontPx = Math.round(state.style.font_size * d);
    const font = (weight: number, px: number): string =>
      `${weight} ${px}px system-ui, -apple-system, sans-serif`;

    // The chart's Legend setting (⚙ Chart, remembered per person) hides the
    // painted legend like it hides uPlot's DOM one elsewhere; the ring then
    // takes the space, and hover still names each slice.
    const legendRows = !state.style.show_legend ? [] : [
      ...slices.map((sl) => ({
        color: sl.color,
        name: sl.label,
        // Signals carry their own figure (the mean or the sum); a band's figure
        // is a row count, which the share already says better.
        value: data.mode === 'signals' ? compactNum(sl.value) : '',
        detail: formatShare(sl.share),
        out: false,
      })),
      ...data.excluded.map((x) => ({
        color: '',
        name: x.name,
        value: '',
        detail: x.reason.split(' - ')[0] ?? 'left out',
        out: true,
      })),
    ];
    ctx.save();
    ctx.font = font(600, fontPx);
    const shareW = ctx.measureText('100%').width;
    ctx.font = font(500, fontPx);
    const valueW = legendRows.reduce((m, row) => Math.max(m, ctx.measureText(row.value).width), 0);
    const widest = legendRows.reduce(
      (m, row) =>
        Math.max(
          m,
          ctx.measureText(row.name).width +
            (row.out ? ctx.measureText(row.detail).width : shareW + valueW) +
            fontPx * 2,
        ),
      0,
    );
    const layout = pieLayout(u.bbox, legendRows.length, fontPx, Math.ceil(widest) + fontPx);
    geometry.layout = layout;
    const { cx, cy, r } = layout;
    const inner = donut ? r * DONUT_HOLE : 0;
    const gap = SLICE_GAP_PX * d;

    // Faint full ring first, so a pie of one tiny slice still reads as a circle.
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, TAU);
    if (inner) ctx.arc(cx, cy, inner, TAU, 0, true);
    ctx.fillStyle = c.grid;
    ctx.globalAlpha = 0.25;
    ctx.fill('evenodd');
    ctx.globalAlpha = 1;

    const visible = slices.filter((sl) => sl.share > 0);
    slices.forEach((sl, i) => {
      if (sl.share <= 0) return;
      const pull = i === hover.index ? HOVER_PULL_PX * d : 0;
      const mid = (sl.a0 + sl.a1) / 2;
      const ox = Math.cos(mid) * pull;
      const oy = Math.sin(mid) * pull;
      ctx.beginPath();
      if (visible.length === 1) {
        ctx.arc(cx, cy, r, 0, TAU);
        if (inner) ctx.arc(cx, cy, inner, TAU, 0, true);
      } else {
        ctx.arc(cx + ox, cy + oy, r, sl.a0, sl.a1);
        if (inner) ctx.arc(cx + ox, cy + oy, inner, sl.a1, sl.a0, true);
        else ctx.lineTo(cx + ox, cy + oy);
        ctx.closePath();
      }
      ctx.fillStyle = sl.color;
      ctx.fill('evenodd');
      if (visible.length > 1) {
        // Separators in the surface colour, so neighbouring slices never merge.
        ctx.strokeStyle = c.surface;
        ctx.lineWidth = gap;
        ctx.stroke();
      }
    });

    // Share on the slice where it fits; the legend always carries it too.
    const ringMid = inner ? (r + inner) / 2 : r * 0.62;
    const labelPx = Math.max(Math.round(9 * d), Math.round(fontPx * 0.92));
    ctx.font = font(600, labelPx);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    slices.forEach((sl, i) => {
      const span = sl.a1 - sl.a0;
      if (span < LABEL_MIN_ANGLE || sl.share <= 0) return;
      const text = formatShare(sl.share);
      const thickness = inner ? r - inner : r;
      if (thickness < labelPx * 1.6 || ringMid * span < ctx.measureText(text).width + 6 * d) return;
      const pull = i === hover.index ? HOVER_PULL_PX * d : 0;
      const mid = (sl.a0 + sl.a1) / 2;
      ctx.fillStyle = textOn(sl.color);
      ctx.fillText(
        text,
        cx + Math.cos(mid) * (ringMid + pull),
        cy + Math.sin(mid) * (ringMid + pull),
      );
    });

    // The total in the hole: the one number a donut has room for.
    if (inner > fontPx * 2.2) {
      const hovered = hover.index >= 0 ? slices[hover.index] : undefined;
      const big = Math.min(Math.round(inner * 0.42), Math.round(fontPx * 2));
      ctx.fillStyle = c.textStrong;
      ctx.font = font(600, big);
      const headline = hovered ? formatShare(hovered.share) : compactNum(data.total);
      ctx.fillText(headline, cx, cy - big * 0.22);
      ctx.fillStyle = c.text;
      ctx.font = font(500, Math.max(Math.round(9 * d), Math.round(fontPx * 0.85)));
      const caption = hovered ? hovered.label : centreCaption(data);
      ctx.fillText(ellipsize(ctx, caption, inner * 1.6), cx, cy + big * 0.62);
    }

    // Legend.
    const lg = layout.legend;
    const sw = Math.round(fontPx * 0.8);
    ctx.textBaseline = 'middle';
    const shown = legendRows.slice(0, lg.rows);
    const hiddenCount = legendRows.length - shown.length;
    shown.forEach((row, i) => {
      const y = lg.y + i * lg.rowH + lg.rowH / 2;
      const isLastWithMore = hiddenCount > 0 && i === shown.length - 1;
      if (isLastWithMore) {
        ctx.fillStyle = c.text;
        ctx.font = font(500, fontPx);
        ctx.textAlign = 'left';
        ctx.fillText(`+${hiddenCount + 1} more - hover the chart`, lg.x, y);
        return;
      }
      const emphasised = !row.out && i === hover.index;
      if (row.out) {
        ctx.strokeStyle = c.text;
        ctx.lineWidth = d;
        ctx.strokeRect(lg.x + 0.5, y - sw / 2 + 0.5, sw - 1, sw - 1);
      } else {
        ctx.fillStyle = row.color;
        ctx.fillRect(lg.x, y - sw / 2, sw, sw);
      }
      const textX = lg.x + sw + Math.round(fontPx * 0.6);
      ctx.font = font(600, fontPx);
      ctx.textAlign = 'right';
      ctx.fillStyle = row.out ? c.text : c.textStrong;
      ctx.fillText(row.detail, lg.x + lg.w, y);
      let detailW = row.out ? ctx.measureText(row.detail).width : shareW;
      if (row.value) {
        ctx.font = font(500, fontPx);
        ctx.fillStyle = c.text;
        ctx.fillText(row.value, lg.x + lg.w - shareW - fontPx, y);
        detailW += valueW + fontPx;
      }
      ctx.font = font(emphasised ? 600 : 500, fontPx);
      ctx.textAlign = 'left';
      ctx.fillStyle = row.out ? c.text : emphasised ? c.textStrong : c.text;
      ctx.globalAlpha = row.out ? 0.75 : 1;
      ctx.fillText(ellipsize(ctx, row.name, lg.w - (textX - lg.x) - detailW - fontPx), textX, y);
      ctx.globalAlpha = 1;
    });
    ctx.restore();
  };
}

/** Slice under the cursor, or -1. Cursor positions are CSS px; the layout is canvas px. */
function sliceUnderCursor(
  u: uPlot,
  geometry: { layout: PieLayout | null; slices: DrawnSlice[] },
  donut: boolean,
): number {
  const layout = geometry.layout;
  const left = u.cursor.left;
  const top = u.cursor.top;
  if (!layout || left == null || top == null || left < 0 || top < 0) return -1;
  const d = uPlot.pxRatio || 1;
  const px = left * d + u.bbox.left;
  const py = top * d + u.bbox.top;
  const dx = px - layout.cx;
  const dy = py - layout.cy;
  const dist = Math.hypot(dx, dy);
  const reach = layout.r + HOVER_PULL_PX * d;
  if (dist > reach || (donut && dist < layout.r * DONUT_HOLE)) {
    // Over the legend: the row under the pointer stands for its slice.
    const lg = layout.legend;
    if (px >= lg.x && px <= lg.x + lg.w && py >= lg.y && py < lg.y + lg.rows * lg.rowH) {
      const row = Math.floor((py - lg.y) / lg.rowH);
      return row < geometry.slices.length ? row : -1;
    }
    return -1;
  }
  return sliceIndexAt(geometry.slices, Math.atan2(dy, dx));
}

/** Slice readout, written straight into the tooltip node like the correlation one. */
function pieTooltipHook(
  data: PieResponse,
  state: PlotState,
  hover: PieHover,
  geometry: { layout: PieLayout | null; slices: DrawnSlice[] },
  target?: { el: HTMLElement; container: HTMLElement },
): (u: uPlot) => void {
  return (u: uPlot): void => {
    const tt = target?.el ?? runtime.tooltipEl;
    const idx = u.over ? sliceUnderCursor(u, geometry, state.style.pie_donut) : -1;
    if (idx !== hover.index) {
      hover.index = idx;
      u.redraw(false, false);
    }
    if (!tt) return;
    const sl = idx >= 0 ? geometry.slices[idx] : undefined;
    if (!sl) {
      tt.classList.remove('visible');
      tt.dataset.ttIdx = '';
      return;
    }
    const key = `pie:${idx}`;
    if (tt.dataset.ttIdx !== key) {
      const row = (name: string, val: string): string =>
        `<div class="uptt-row"><span class="uptt-name">${escHtml(name)}</span>` +
        `<span class="uptt-val">${escHtml(val)}</span></div>`;
      const title =
        data.mode === 'bands' ? `${data.column ?? ''} ${sl.label}` : sl.label;
      const rows = [`<div class="uptt-time">${escHtml(title)}</div>`];
      rows.push(row('share', `${(sl.share * 100).toFixed(1)}%`));
      if (data.mode === 'signals') {
        rows.push(row(data.agg === 'sum' ? 'sum' : 'mean', compactNum(sl.value)));
      }
      rows.push(row('rows', sl.count.toLocaleString()));
      tt.innerHTML = rows.join('');
      tt.dataset.ttIdx = key;
    }
    tt.classList.add('visible');

    const container = target?.container ?? runtime.containerEl;
    if (container) placeTooltip(u, tt, container);
  };
}

/**
 * uPlot options for a pie. `size`, the tooltip target and `state` let a grid
 * panel or a dashboard tile reuse this drawing.
 */
export function buildPieOpts(
  data: PieResponse,
  c: ThemeColors,
  size: { width: number; height: number },
  target?: { el: HTMLElement; container: HTMLElement },
  state?: PlotState,
): { opts: uPlot.Options; uData: uPlot.AlignedData } {
  const s = state ?? plotState.value;
  const hover: PieHover = { index: -1 };
  const geometry: { layout: PieLayout | null; slices: DrawnSlice[] } = { layout: null, slices: [] };
  // uPlot wants an x array and one y array; the pie is painted by hand.
  const uData: uPlot.AlignedData = [
    [0, 1],
    [null, null],
  ];
  const hidden = { show: false } as uPlot.Axis;
  const opts: uPlot.Options = {
    ...size,
    padding: [8, 8, 8, 8],
    scales: {
      x: { time: false, range: (): [number, number] => [0, 1] },
      y: { range: (): [number, number] => [0, 1] },
    },
    axes: [hidden, { ...hidden }],
    series: [{}, { label: 'pie', paths: () => null, points: { show: false } }],
    legend: { show: false },
    cursor: {
      x: false,
      y: false,
      points: { show: false },
      drag: { x: false, y: false },
    },
    hooks: {
      draw: [pieDrawHook(data, c, s, hover, geometry)],
      setCursor: [pieTooltipHook(data, s, hover, geometry, target)],
    },
  };
  return { opts, uData };
}

/**
 * No strip under a pie. Bands are coloured off the ramp, but evenly by band
 * rather than by value, so a min-to-max strip would suggest a mapping that is
 * not there - the legend on the canvas names every colour instead.
 */
export function pieLegend(): void {
  colorLegend.set(null);
}

export function renderPie(data: PieResponse): void {
  const c = themeColors();
  const { opts, uData } = buildPieOpts(data, c, chartContainerSize());
  rebuildUplot(uData, opts);
  pieLegend();
}
